import React, { useEffect, useState } from 'react';
import { Select } from 'antd';
import { C } from '../../constants/design-tokens';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';
import { useListMusicTracks } from '../../../../hooks/audio/use-list-music-tracks';

const NO_MUSIC_VALUE = '';

type TrackOption = { id: string; name: string; url: string };

interface MusicTrackSelectorProps {
  selected: string | null;
  onChange: (id: string | null) => void;
}

/**
 * Dropdown para elegir un track de música de fondo. Trae las opciones desde
 * `GET /audio/music` (mismo endpoint que usa el SoundPanel del editor) y
 * siempre agrega "Sin música" como default. La duración y el gain (50%) los
 * resuelve el editor al bootear — acá solo elegimos qué track.
 *
 * Si el cliente no tiene tracks configurados en su librería, el selector se
 * oculta: mostrar un dropdown con solo "Sin música" es ruido inútil.
 */
export const MusicTrackSelector: React.FC<MusicTrackSelectorProps> = ({ selected, onChange }) => {
  const { spacing, fontSize } = useResponsiveSizes();
  const { listMusicTracks } = useListMusicTracks();
  const [tracks, setTracks] = useState<TrackOption[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    listMusicTracks().then(res => {
      if (cancelled) return;
      setTracks((res?.tracks ?? []).map(t => ({ id: t.id, name: t.name, url: t.url })));
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [listMusicTracks]);

  // Ocultar mientras carga o si el cliente no tiene tracks.
  if (isLoading || !tracks || tracks.length === 0) return null;

  const options = [
    { value: NO_MUSIC_VALUE, label: 'Sin música' },
    ...tracks.map(t => ({ value: t.id, label: t.name }))
  ];

  const selectedTrack = selected ? (tracks.find(t => t.id === selected) ?? null) : null;

  return (
    <div style={{ marginBottom: spacing.lg }}>
      <div style={{ marginBottom: spacing.sm }}>
        <span
          style={{
            fontSize: fontSize.sm,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            color: C.textLabel
          }}
        >
          Música de fondo
        </span>
      </div>
      <Select<string>
        value={selected ?? NO_MUSIC_VALUE}
        onChange={v => onChange(v === NO_MUSIC_VALUE ? null : v)}
        options={options}
        style={{ width: '100%' }}
      />
      {selectedTrack && (
        <audio
          key={selectedTrack.id}
          src={selectedTrack.url}
          controls
          preload='metadata'
          style={{ width: '100%', marginTop: spacing.sm }}
        />
      )}
    </div>
  );
};
